const BiotechUnit4Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-indigo-900 mb-3 border-b-2 border-slate-200 pb-2">Unit IV: Blotting Techniques & Microbial Genetics</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit bridges molecular biology and microbiology. It covers the analytical techniques used to detect specific DNA or proteins (Blotting & ELISA), how microbes organize and share their genetic material, and the concepts of genetic mutation and biotransformation.
        </p>
    </div>

    <!-- 2. Immuno-blotting Techniques -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">1. Immuno-blotting Techniques</h3>
        <p class="text-[13px] text-slate-700 mb-4">Blotting is a technique used to transfer macromolecules (DNA, RNA, or Proteins) from a gel to a solid membrane, followed by their detection using specific probes or antibodies.</p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <!-- Southern Blotting -->
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                <h4 class="font-bold text-blue-900 text-[15px] mb-2">A. Southern Blotting</h4>
                <p class="text-[12px] text-slate-700 mb-2">Used for the detection of specific <strong>DNA sequences</strong> in a complex DNA sample.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>Developed by Edwin Southern (1975).</li>
                    <li>DNA is cut, separated by gel electrophoresis, transferred to a nitrocellulose membrane, and hybridized with a radioactive/fluorescent DNA probe.</li>
                    <li><em>Application:</em> DNA fingerprinting, genetic disease diagnosis.</li>
                </ul>
            </div>

            <!-- Western Blotting -->
            <div class="bg-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm">
                <h4 class="font-bold text-indigo-900 text-[15px] mb-2">B. Western Blotting</h4>
                <p class="text-[12px] text-slate-700 mb-2">Used for the detection of specific <strong>Proteins</strong> in a tissue homogenate or extract.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>Proteins are separated by size using SDS-PAGE, transferred to a membrane, and incubated with specific <strong>antibodies</strong>.</li>
                    <li>A secondary antibody linked to an enzyme provides a visible signal.</li>
                    <li><em>Application:</em> Confirmatory test for HIV/AIDS, detecting Lyme disease.</li>
                </ul>
            </div>

            <!-- ELISA -->
            <div class="bg-purple-50 p-4 rounded-lg border border-purple-100 shadow-sm">
                <h4 class="font-bold text-purple-900 text-[15px] mb-2">C. ELISA</h4>
                <p class="text-[12px] text-slate-700 mb-2">Enzyme-Linked Immunosorbent Assay. A plate-based technique designed for detecting and quantifying peptides, proteins, antibodies, and hormones.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>An antigen is immobilized on a microplate and bound to a specific antibody which is linked to a reporter enzyme.</li>
                    <li>Addition of substrate produces a measurable color change.</li>
                    <li><em>Application:</em> Pregnancy tests, COVID-19 antibody tests.</li>
                </ul>
            </div>
        </div>
        <p class="text-[12px] text-slate-500 italic text-center font-bold">Mnemonic to remember: SNoW DRoP (Southern=DNA, Northern=RNA, Western=Protein)</p>
    </div>

    <!-- 3. Genetic Organization -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">2. Genetic Organization: Eukaryotes vs. Prokaryotes</h3>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-indigo-100 text-indigo-900">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/4">Feature</th>
                        <th class="px-4 py-2.5 font-bold w-1/3">Prokaryotes (e.g., Bacteria)</th>
                        <th class="px-4 py-2.5 font-bold w-1/3">Eukaryotes (e.g., Humans, Yeast)</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-indigo-800">Nucleus</td>
                        <td class="px-4 py-3">Absent. DNA is freely floating in the <strong>Nucleoid</strong> region.</td>
                        <td class="px-4 py-3">Present. DNA is enclosed within a true nuclear membrane.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-indigo-800">DNA Structure</td>
                        <td class="px-4 py-3">Usually a single, circular, double-stranded chromosome. Lacks histones.</td>
                        <td class="px-4 py-3">Multiple, linear chromosomes. DNA is wrapped around histone proteins.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-indigo-800">Extra-chromosomal DNA</td>
                        <td class="px-4 py-3">Frequently present in the form of <strong>Plasmids</strong>.</td>
                        <td class="px-4 py-3">Present in mitochondria and chloroplasts.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-indigo-800">Introns & Exons</td>
                        <td class="px-4 py-3">Introns (non-coding sequences) are mostly absent. Genes are continuous.</td>
                        <td class="px-4 py-3">Genes contain both Exons (coding) and Introns (non-coding). Splicing is required.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- 4. Microbial Genetics -->
    <div class="bg-indigo-50 p-6 border border-indigo-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-900 mb-4 border-b border-indigo-200 pb-2">3. Microbial Genetics (Gene Transfer Mechanisms)</h3>
        <p class="text-[13px] text-slate-700 mb-4">Bacteria reproduce asexually (binary fission), but they can acquire new genetic traits (like antibiotic resistance) through <strong>Horizontal Gene Transfer</strong>.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div class="bg-white p-4 rounded shadow-sm border border-indigo-100">
                <h4 class="font-bold text-indigo-800 text-[15px] mb-1">1. Transformation</h4>
                <p class="text-[12px] text-slate-600">The uptake of <strong>naked DNA</strong> fragments from the surrounding environment by a competent bacterial cell. (Discovered by Griffith in Streptococcus pneumoniae).</p>
            </div>
            <div class="bg-white p-4 rounded shadow-sm border border-indigo-100">
                <h4 class="font-bold text-indigo-800 text-[15px] mb-1">2. Transduction</h4>
                <p class="text-[12px] text-slate-600">The transfer of bacterial DNA from one cell to another via a viral vector (a <strong>Bacteriophage</strong>). Can be generalized or specialized.</p>
            </div>
            <div class="bg-white p-4 rounded shadow-sm border border-indigo-100">
                <h4 class="font-bold text-indigo-800 text-[15px] mb-1">3. Conjugation</h4>
                <p class="text-[12px] text-slate-600">Direct cell-to-cell contact. DNA (usually a plasmid) is transferred from a donor (F+) to a recipient (F-) through a connecting tube called a <strong>Sex Pilus</strong>.</p>
            </div>
        </div>

        <div class="bg-white p-4 rounded border border-indigo-200 text-[13px] text-slate-700">
            <p><strong class="text-indigo-900">Plasmids:</strong> Small, circular, extra-chromosomal DNA molecules that replicate independently. They often carry genes conferring an evolutionary advantage (e.g., R-plasmids carry antibiotic resistance).</p>
            <p class="mt-2"><strong class="text-indigo-900">Transposons:</strong> "Jumping Genes". Segments of DNA that can move (transpose) from one location in the genome to another, causing mutations or altering gene expression.</p>
        </div>
    </div>

    <!-- 5. Mutation & Biotransformation -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">4. Mutation & Microbial Biotransformation</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Mutation -->
            <div>
                <h4 class="text-[16px] font-bold text-indigo-900 mb-2">A. Mutation</h4>
                <p class="text-[12px] text-slate-700 mb-2">A sudden, heritable change in the nucleotide sequence of DNA. Organisms possessing mutations are called <em>mutants</em>.</p>
                <div class="bg-slate-50 p-3 rounded border border-slate-200">
                    <p class="font-bold text-indigo-800 text-[13px] mb-1">Types of Point Mutations:</p>
                    <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                        <li><strong>Silent Mutation:</strong> Base change does not alter the amino acid produced (due to codon degeneracy).</li>
                        <li><strong>Missense Mutation:</strong> Base change results in the incorporation of a <em>different</em> amino acid, potentially altering protein function.</li>
                        <li><strong>Nonsense Mutation:</strong> Base change creates a <em>stop codon</em>, resulting in a prematurely truncated, usually non-functional protein.</li>
                        <li><strong>Frameshift Mutation:</strong> Insertion or deletion of bases (not in multiples of 3) that shifts the entire reading frame of the gene.</li>
                    </ul>
                </div>
            </div>

            <!-- Biotransformation -->
            <div>
                <h4 class="text-[16px] font-bold text-indigo-900 mb-2">B. Microbial Biotransformation</h4>
                <p class="text-[12px] text-slate-700 mb-2">The specific modification of a chemical compound to a structurally related product using the enzymatic machinery of microbes. It is a one-step, highly specific biological reaction.</p>
                <div class="bg-slate-50 p-3 rounded border border-slate-200">
                    <p class="font-bold text-indigo-800 text-[13px] mb-1">Advantages & Applications:</p>
                    <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                        <li>Reactions occur under mild conditions (room temp, neutral pH).</li>
                        <li>Provides high <strong>stereo- and regio-specificity</strong> which is extremely difficult via chemical synthesis.</li>
                        <li><em>Major Application:</em> <strong>Steroid Biotransformation.</strong> E.g., 11&alpha;-hydroxylation of progesterone by <em>Rhizopus nigricans</em>, which drastically reduced the cost of synthesizing cortisone.</li>
                        <li>Synthesis of L-Ascorbic acid (Vitamin C).</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

</div>
`;

export default BiotechUnit4Notes;